import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import { LineChart, X, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import type { TickerResult, LoadingTicker } from './ReturnsTable';
import type { GroupedTickerData } from '@/types/grouping';
import {
  COLUMN_GROUPS,
  getOrderedGroups,
  getVisibleColumnsInGroup,
  isFirstColumnInGroup,
  buildOrderedColumnList,
  getColumnGroup,
} from './columnGroups';
import { DEFAULT_VISIBLE_COLUMNS } from './ReturnsTable';

export interface GroupedTableProps {
  groups: GroupedTickerData<TickerResult>[];
  className?: string;
  onRowClick?: (ticker: string) => void;
  visibleColumns?: string[];
  onRemoveTicker?: (ticker: string) => void;
  loadingTickers?: LoadingTicker[];
  groupOrder?: string[];
}

// Format number as currency (no decimals for cleaner display)
const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format number as percentage (1 decimal place)
const formatPercent = (value: number | null | undefined, showSign = true): string => {
  if (value == null) return '-';
  const sign = showSign && value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Format date
const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const columnHelper = createColumnHelper<TickerResult>();

/**
 * GroupHeader component for expandable group rows
 */
interface GroupHeaderProps {
  group: GroupedTickerData<TickerResult>;
  isExpanded: boolean;
  onToggle: () => void;
  visibleColumns: string[];
  orderedColumnList: string[];
  onRowClick?: (ticker: string) => void;
}

function GroupHeader({
  group,
  isExpanded,
  onToggle,
  visibleColumns,
  orderedColumnList,
  onRowClick,
}: GroupHeaderProps) {
  const { groupLabel, aggregates } = group;

  // Render aggregated value for a column
  const renderAggregate = (columnId: string): React.ReactNode => {
    switch (columnId) {
      case 'ticker':
        return (
          <div className="flex items-center gap-2 font-semibold text-foreground">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span>{groupLabel}</span>
            <span className="text-muted-foreground font-normal text-sm">
              ({aggregates.count} {aggregates.count === 1 ? 'ticker' : 'tickers'})
            </span>
          </div>
        );
      case 'totalReturn':
      case 'cagr':
        const value = columnId === 'totalReturn' ? aggregates.avgTotalReturn : aggregates.avgCAGR;
        const colorClass = value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
        return <span className={`font-medium ${colorClass}`}>{formatPercent(value)} avg</span>;
      case 'dividendsReceived':
        return <span className="text-muted-foreground">{formatCurrency(aggregates.totalDividends)} total</span>;
      case 'dividendYield':
        return <span className="text-muted-foreground">{formatPercent(aggregates.avgDividendYield, false)} avg</span>;
      case 'volatilityPercent':
        return <span className="text-muted-foreground">{formatPercent(aggregates.avgVolatility, false)} avg</span>;
      default:
        return <span className="text-muted-foreground">-</span>;
    }
  };

  return (
    <tr
      className="bg-muted/70 hover:bg-muted transition-colors cursor-pointer"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={isExpanded}
      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${groupLabel} group`}
    >
      {orderedColumnList.map((columnId) => {
        const isFirstInGroup = isFirstColumnInGroup(columnId, visibleColumns);
        return (
          <td
            key={columnId}
            className={`truncate px-2 py-2 text-sm
              ${isFirstInGroup ? 'border-l-2 border-slate-600' : ''}
            `}
          >
            {renderAggregate(columnId)}
          </td>
        );
      })}
      {/* Action columns */}
      <td className="px-2 py-2 text-sm border-l-2 border-slate-600">
        {aggregates.bestPerformer && (
          <a
            href={`https://totalrealreturns.com/s/${aggregates.bestPerformer}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex justify-center items-center p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title={`View best performer (${aggregates.bestPerformer}) on Total Real Returns`}
            aria-label={`View best performer (${aggregates.bestPerformer}) on Total Real Returns`}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </td>
      {onRowClick && (
        <td className="px-2 py-2 text-sm" />
      )}
      <td className="px-2 py-2 text-sm" />
    </tr>
  );
}

export const GroupedTable: React.FC<GroupedTableProps> = ({
  groups,
  className = '',
  onRowClick,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
  onRemoveTicker,
  loadingTickers = [],
  groupOrder = COLUMN_GROUPS.map((g) => g.id),
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(groups.map((g) => g.groupKey))
  );

  // Get ordered groups that have visible columns
  const orderedGroups = useMemo(
    () => getOrderedGroups(groupOrder, visibleColumns),
    [groupOrder, visibleColumns]
  );

  // Build ordered column list based on group order
  const orderedColumnList = useMemo(
    () => buildOrderedColumnList(groupOrder, visibleColumns),
    [groupOrder, visibleColumns]
  );

  // Flatten all tickers from expanded groups for the table
  const flattenedData = useMemo(() => {
    const result: Array<{ ticker: TickerResult; groupKey: string }> = [];
    for (const group of groups) {
      if (expandedGroups.has(group.groupKey)) {
        for (const ticker of group.tickers) {
          result.push({ ticker, groupKey: group.groupKey });
        }
      }
    }
    return result;
  }, [groups, expandedGroups]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const columns = useMemo<ColumnDef<TickerResult, any>[]>(
    () => {
      const allColumns: Record<string, ColumnDef<TickerResult, any>> = {
        ticker: columnHelper.accessor('ticker', {
          id: 'ticker',
          header: () => 'Ticker',
          cell: (info) => (
            <span className="font-semibold text-foreground pl-4">{info.getValue()}</span>
          ),
          sortingFn: 'alphanumeric',
        }),
        totalReturn: columnHelper.accessor('totalReturn', {
          id: 'totalReturn',
          header: () => 'Total Return',
          cell: (info) => {
            const value = info.getValue();
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={`font-medium ${colorClass}`}>
                {formatPercent(value)}
              </span>
            );
          },
          sortingFn: 'basic',
        }),
        cagr: columnHelper.accessor('cagr', {
          id: 'cagr',
          header: () => 'CAGR',
          cell: (info) => {
            const value = info.getValue();
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={`font-medium ${colorClass}`}>
                {formatPercent(value)}
              </span>
            );
          },
          sortingFn: 'basic',
        }),
        finalValue: columnHelper.accessor('finalValue', {
          id: 'finalValue',
          header: () => 'Final (100% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        dividendsReceived: columnHelper.accessor('dividendsReceived', {
          id: 'dividendsReceived',
          header: () => 'Dividends',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        inceptionDate: columnHelper.accessor('inceptionDate', {
          id: 'inceptionDate',
          header: () => 'Inception Date',
          cell: (info) => (
            <span className="text-muted-foreground">{formatDate(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        finalValue0Percent: columnHelper.accessor('finalValue0Percent', {
          id: 'finalValue0Percent',
          header: () => 'Final (0% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        finalValue50Percent: columnHelper.accessor('finalValue50Percent', {
          id: 'finalValue50Percent',
          header: () => 'Final (50% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        flatThresholdPercent: columnHelper.accessor('flatThresholdPercent', {
          id: 'flatThresholdPercent',
          header: () => 'Flat Threshold',
          cell: (info) => (
            <span className="text-muted-foreground">{formatPercent(info.getValue(), false)}</span>
          ),
          sortingFn: 'basic',
        }),
        dividendYield: columnHelper.accessor('dividendYield', {
          id: 'dividendYield',
          header: () => 'Div Yield',
          cell: (info) => (
            <span className="text-muted-foreground">{formatPercent(info.getValue(), false)}</span>
          ),
          sortingFn: 'basic',
        }),
        maxDrawdownPercent: columnHelper.accessor('maxDrawdownPercent', {
          id: 'maxDrawdownPercent',
          header: () => 'Max Drawdown',
          cell: (info) => {
            const value = info.getValue();
            return (
              <span className="text-red-600 dark:text-red-400">
                {value != null ? `-${Math.abs(value).toFixed(1)}%` : '-'}
              </span>
            );
          },
          sortingFn: 'basic',
        }),
        volatilityPercent: columnHelper.accessor('volatilityPercent', {
          id: 'volatilityPercent',
          header: () => 'Volatility',
          cell: (info) => (
            <span className="text-muted-foreground">{formatPercent(info.getValue(), false)}</span>
          ),
          sortingFn: 'basic',
        }),
        dividendGrowthRatePercent: columnHelper.accessor('dividendGrowthRatePercent', {
          id: 'dividendGrowthRatePercent',
          header: () => 'Div Growth',
          cell: (info) => {
            const value = info.getValue();
            if (value == null) return <span className="text-muted-foreground">-</span>;
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={colorClass}>{formatPercent(value)}</span>
            );
          },
          sortingFn: 'basic',
        }),
        underlyingTicker: columnHelper.accessor('underlyingTicker', {
          id: 'underlyingTicker',
          header: () => 'Underlying',
          cell: (info) => (
            <span className="text-muted-foreground font-mono">{info.getValue() || '-'}</span>
          ),
          sortingFn: 'alphanumeric',
        }),
        startPrice: columnHelper.accessor('startPrice', {
          id: 'startPrice',
          header: () => 'Start Price',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        endPrice: columnHelper.accessor('endPrice', {
          id: 'endPrice',
          header: () => 'End Price',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        priceChangeDollars: columnHelper.accessor('priceChangeDollars', {
          id: 'priceChangeDollars',
          header: () => 'Price Δ ($)',
          cell: (info) => {
            const value = info.getValue();
            if (value == null) return <span className="text-muted-foreground">-</span>;
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            const sign = value >= 0 ? '+' : '';
            return (
              <span className={colorClass}>{sign}{formatCurrency(value)}</span>
            );
          },
          sortingFn: 'basic',
        }),
        priceChangePercent: columnHelper.accessor('priceChangePercent', {
          id: 'priceChangePercent',
          header: () => 'Price Δ (%)',
          cell: (info) => {
            const value = info.getValue();
            if (value == null) return <span className="text-muted-foreground">-</span>;
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={colorClass}>{formatPercent(value)}</span>
            );
          },
          sortingFn: 'basic',
        }),
      };

      // Filter and order columns based on group order and visibility
      const filteredColumns: ColumnDef<TickerResult, any>[] = orderedColumnList
        .filter(colId => allColumns[colId])
        .map(colId => allColumns[colId]);

      // Add Total Real Returns link column (always visible)
      filteredColumns.push(
        columnHelper.display({
          id: 'externalLink',
          header: () => <span className="sr-only">Total Real Returns</span>,
          cell: (info) => (
            <a
              href={`https://totalrealreturns.com/s/${info.row.original.ticker}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex justify-center items-center p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title={`View ${info.row.original.ticker} on Total Real Returns`}
              aria-label={`View ${info.row.original.ticker} on Total Real Returns`}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          ),
        })
      );

      // Add chart icon column if onRowClick is provided
      if (onRowClick) {
        filteredColumns.push(
          columnHelper.display({
            id: 'chartIcon',
            header: () => <span className="sr-only">View Chart</span>,
            cell: () => (
              <span className="flex justify-center">
                <LineChart className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </span>
            ),
          })
        );
      }

      // Add remove button column if onRemoveTicker is provided
      if (onRemoveTicker) {
        filteredColumns.push(
          columnHelper.display({
            id: 'removeAction',
            header: () => <span className="sr-only">Remove</span>,
            cell: (info) => (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveTicker(info.row.original.ticker);
                }}
                className="flex justify-center items-center p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title={`Remove ${info.row.original.ticker}`}
                aria-label={`Remove ${info.row.original.ticker}`}
              >
                <X className="h-4 w-4" />
              </button>
            ),
          })
        );
      }

      return filteredColumns;
    },
    [onRowClick, onRemoveTicker, orderedColumnList]
  );

  const table = useReactTable({
    data: flattenedData.map((d) => d.ticker),
    columns,
    getRowId: (originalRow) => originalRow.ticker,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleRowClick = (ticker: string) => {
    if (onRowClick) {
      onRowClick(ticker);
    }
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, ticker: string) => {
    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onRowClick(ticker);
    }
  };

  // Show empty state only if no groups AND no loading tickers
  if (groups.length === 0 && loadingTickers.length === 0) {
    return (
      <div
        className={`bg-muted rounded-lg border border-border p-8 text-center ${className}`}
        role="status"
        aria-label="No data available"
      >
        <p className="text-muted-foreground text-lg">No data available to display</p>
      </div>
    );
  }

  // Count action columns (externalLink is always present)
  const actionColumnCount = 1 + (onRowClick ? 1 : 0) + (onRemoveTicker ? 1 : 0);
  const visibleColumnCount = orderedColumnList.length + actionColumnCount;

  return (
    <div
      className={`w-full rounded-lg border border-border shadow-sm overflow-hidden ${className}`}
    >
      <table
        className="w-full table-fixed divide-y divide-border"
        role="table"
        aria-label="Grouped returns table"
      >
        <thead>
          {/* Group header row */}
          <tr className="border-b border-slate-600">
            {orderedGroups.map((group, idx) => {
              const visibleInGroup = getVisibleColumnsInGroup(group, visibleColumns);
              return (
                <th
                  key={group.id}
                  colSpan={visibleInGroup.length}
                  className={`px-2 py-2 text-center text-xs font-bold uppercase tracking-wider text-foreground truncate
                    ${group.colorClass}
                    ${idx > 0 ? 'border-l-2 border-slate-500' : ''}
                  `}
                >
                  {group.label}
                </th>
              );
            })}
            {/* Empty header for action columns */}
            {actionColumnCount > 0 && (
              <th
                colSpan={actionColumnCount}
                className="bg-muted px-3 py-2 border-l-2 border-slate-500"
              >
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>

          {/* Column header row */}
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-muted">
              {headerGroup.headers.map((header) => {
                const columnId = header.column.id;
                const group = getColumnGroup(columnId);
                const isFirstInGroup = isFirstColumnInGroup(columnId, visibleColumns);
                const isActionColumn = columnId === 'chartIcon' || columnId === 'removeAction';

                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={`px-2 py-2 text-left text-xs font-semibold text-foreground truncate
                      ${group ? group.headerColorClass : 'bg-muted'}
                      ${(isFirstInGroup || (isActionColumn && !group)) ? 'border-l-2 border-slate-500' : ''}
                      ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-accent/50 transition-colors' : ''}
                    `}
                    onClick={header.column.getToggleSortingHandler()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        header.column.getToggleSortingHandler()?.(e);
                      }
                    }}
                    tabIndex={header.column.getCanSort() ? 0 : undefined}
                    aria-sort={
                      header.column.getIsSorted()
                        ? header.column.getIsSorted() === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <div className="flex items-center gap-2">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getCanSort() && (
                        <span className="text-muted-foreground">
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? ' ↕'}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {groups.map((group) => (
            <React.Fragment key={group.groupKey}>
              {/* Group header row */}
              <GroupHeader
                group={group}
                isExpanded={expandedGroups.has(group.groupKey)}
                onToggle={() => toggleGroup(group.groupKey)}
                visibleColumns={visibleColumns}
                orderedColumnList={orderedColumnList}
                onRowClick={onRowClick}
              />

              {/* Ticker rows (if expanded) */}
              {expandedGroups.has(group.groupKey) &&
                group.tickers.map((ticker, index) => {
                  const row = table.getRowModel().rows.find((r) => r.original.ticker === ticker.ticker);
                  if (!row) return null;

                  const isClickable = !!onRowClick;
                  return (
                    <tr
                      key={ticker.ticker}
                      className={`${
                        index % 2 === 0 ? 'bg-card' : 'bg-muted/30'
                      } ${
                        isClickable
                          ? 'cursor-pointer hover:bg-accent transition-colors'
                          : 'hover:bg-accent/50 transition-colors'
                      }`}
                      onClick={() => handleRowClick(ticker.ticker)}
                      onKeyDown={(e) => handleRowKeyDown(e, ticker.ticker)}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      aria-label={isClickable ? `View chart for ${ticker.ticker}` : undefined}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const columnId = cell.column.id;
                        const isFirstInGroup = isFirstColumnInGroup(columnId, visibleColumns);
                        const isFirstActionCol = columnId === 'externalLink';

                        return (
                          <td
                            key={cell.id}
                            className={`truncate px-2 py-2 text-sm
                              ${isFirstInGroup ? 'border-l-2 border-slate-600' : ''}
                              ${isFirstActionCol ? 'border-l-2 border-slate-600' : ''}
                            `}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </React.Fragment>
          ))}

          {/* Loading rows for tickers being calculated */}
          {loadingTickers.map((loadingTicker, index) => (
            <tr
              key={`loading-${loadingTicker.symbol}`}
              className={`${
                index % 2 === 0 ? 'bg-card' : 'bg-muted/30'
              } animate-pulse`}
            >
              <td className="truncate px-2 py-2 text-sm">
                <span className="font-semibold text-foreground">{loadingTicker.symbol}</span>
              </td>
              <td colSpan={visibleColumnCount - 1} className="truncate px-2 py-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Calculating...</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GroupedTable;
